import { useMemo } from 'react';
import { INDUSTRY_INSIGHTS, useInsightsMenu, useInsightsQuery, useInsightsSearch } from '..';
import { InsightFilterType, InsightType } from '../apis/PythonInsightsAPI';
import useInsightsFilters from './useInsightsFilters';
import _ from 'lodash';
import { getISODateFromDurationString, useBuildWiseModules } from '@buildwise/libs/helpers';
import { GetInsightsQueryFnType, GetInsightsQueryKeyType } from '../queries/useInsightsQuery';
import useUserData from 'hooks/useUserData';
import useInsightsStore from 'store/useInsightsStore';
import { useLocation, useParams } from 'react-router-dom';

function useInsights({
  getInsightsQueryKey,
  getInsightsQueryFn,
}: {
  getRedirectUrl?: (options: { insightType: InsightType }) => string;
  getInsightsQueryFn?: GetInsightsQueryFnType;
  getInsightsQueryKey?: GetInsightsQueryKeyType;
} = {}) {
  const { projectId = '' } = useParams();
  const { pathname } = useLocation();
  const { companyId } = useUserData();
  const { insightsModule, isLoadingBuildWiseModules } = useBuildWiseModules();
  const { isLoadingGroupByValues, menu, setMenu } = useInsightsMenu({ insightsModule });
  const { filters, hasActiveFilters, clearFilters, setFilters, isFiltersLoading } =
    useInsightsFilters({ insightsModule });
  const { searchKeyword, searchInputRef, handleClearSearch, handleSearchKeyDown } =
    useInsightsSearch();
  const { mappedFilters, includeBWInsights } = useMemo(() => {
    let includeBWInsights = false;
    let mappedFilters: InsightFilterType[] = [];
    mappedFilters = _.compact(
      filters.map((filter) => {
        if (filter.category === 'insightType') {
          includeBWInsights =
            !!filter.selectedOptions && filter.selectedOptions?.includes(INDUSTRY_INSIGHTS);
          return;
        }

        if (filter.inputType === 'radio') {
          if (filter.selectedValue) {
            if (filter.category === 'date') {
              return filter.selectedValue !== 'All'
                ? {
                    filterName: 'date',
                    filterValues: [getISODateFromDurationString(filter.selectedValue)],
                  }
                : undefined;
            }

            return {
              filterName: filter.category,
              filterValues: [filter.selectedValue],
            };
          }

          return undefined;
        }

        if (filter.selectedOptions?.length) {
          return {
            filterName: filter.category || '',
            filterValues: filter.selectedOptions || [],
          };
        }

        return undefined;
      }),
    );

    return {
      mappedFilters,
      includeBWInsights,
    };
  }, [filters]);
  const { insights, mappedInsights, ...rest } = useInsightsQuery({
    filters: mappedFilters,
    groupBy: menu.selectedOption.value as InsightType,
    insightsModule,
    includeBWInsights,
    novaSearchKeyWord: searchKeyword,
    projectId,
    getInsightsQueryFn,
    getInsightsQueryKey,
  });
  const { insightsState, projectInsightsState } = useInsightsStore();
  const insightTypeState = useMemo(
    () =>
      pathname.includes('project')
        ? projectInsightsState[menu.selectedOption.value as InsightType]
        : insightsState[menu.selectedOption.value as InsightType],
    [pathname, insightsState, projectInsightsState, menu.selectedOption.value],
  );

  return {
    filters,
    hasActiveFilters,
    includeBWInsights,
    insightTypeState,
    insights,
    insightsModule,
    isFiltersLoading,
    isLoadingBuildWiseModules,
    isLoadingGroupByValues,
    mappedFilters,
    mappedInsights,
    menu,
    searchInputRef,
    searchKeyword,
    ...rest,
    clearFilters,
    handleClearSearch,
    handleSearchKeyDown,
    setFilters,
    setMenu,
  };
}

export default useInsights;
